import React, { useState } from 'react';
import { X, User, Check, Search } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import type { Tool } from '@shared/types/inventory';
import type { Technician } from '@shared/types';

interface ToolAssignmentModalProps {
    isOpen: boolean;
    onClose: () => void;
    tool: Tool | null;
    technicians: Technician[];
    onAssign: (toolId: string, technicianId: string) => void;
    onUnassign: (toolId: string) => void;
}

export const ToolAssignmentModal: React.FC<ToolAssignmentModalProps> = ({
    isOpen, onClose, tool, technicians, onAssign, onUnassign
}) => {
    const [searchTerm, setSearchTerm] = useState('');

    if (!tool) return null;

    const filteredTechs = technicians.filter(t =>
        t.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.service.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const isAssigned = tool.status === 'assigned';
    // Find current assignee if any
    const currentAssignee = technicians.find(t => t.id === tool.assignedTo);

    return (
        <AnimatePresence>
            {isOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                        onClick={onClose}
                    />

                    <motion.div
                        initial={{ scale: 0.95, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0.95, opacity: 0 }}
                        className="bg-[#1e293b] border border-white/10 rounded-2xl w-full max-w-md shadow-2xl overflow-hidden relative z-10"
                    >
                        {/* Header */}
                        <div className="p-4 border-b border-white/10 flex items-center justify-between">
                            <h3 className="font-bold text-white flex items-center gap-2">
                                <User size={18} className="text-primary" />
                                Assignation Matériel
                            </h3>
                            <button onClick={onClose} className="p-1 hover:bg-white/10 rounded-full transition-colors">
                                <X size={20} className="text-gray-400" />
                            </button>
                        </div>

                        {/* Tool Info */}
                        <div className="p-4 bg-white/5 border-b border-white/10 flex items-center gap-3">
                            <div className="w-12 h-12 bg-black/30 rounded-lg flex items-center justify-center">
                                {tool.image ? (
                                    <img src={tool.image} alt={tool.name} className="w-full h-full object-cover rounded-lg" />
                                ) : (
                                    <span className="text-xl">🛠️</span>
                                )}
                            </div>
                            <div>
                                <h4 className="font-bold text-white">{tool.name}</h4>
                                <p className="text-xs text-gray-400">{tool.serialNumber || 'Sans Numéro de Série'}</p>
                            </div>
                        </div>

                        {/* Current Status */}
                        {isAssigned && currentAssignee && (
                            <div className="p-4 border-b border-white/10">
                                <p className="text-xs text-gray-400 uppercase tracking-wide mb-2">Actuellement assigné à</p>
                                <div className="flex items-center justify-between bg-blue-500/10 border border-blue-500/30 p-3 rounded-xl">
                                    <div className="flex items-center gap-3">
                                        <div
                                            className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold text-white shadow-sm"
                                            style={{ backgroundColor: currentAssignee.color }}
                                        >
                                            {currentAssignee.name.charAt(0)}
                                        </div>
                                        <div>
                                            <p className="font-bold text-blue-100">{currentAssignee.name}</p>
                                            <p className="text-xs text-blue-300">{currentAssignee.service}</p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => onUnassign(tool.id)}
                                        className="px-3 py-1.5 bg-white/10 hover:bg-white/20 text-white text-xs font-medium rounded-lg transition-colors"
                                    >
                                        Restituer
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Tech Selection */}
                        <div className="p-4 h-[300px] flex flex-col">
                            <h4 className="text-sm font-medium text-gray-300 mb-3">Sélectionner un technicien</h4>

                            <div className="relative mb-3">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={14} />
                                <input
                                    type="text"
                                    placeholder="Rechercher..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full bg-black/20 border border-white/10 rounded-lg py-2 pl-9 pr-3 text-sm text-white focus:outline-none focus:border-primary/50"
                                />
                            </div>

                            <div className="flex-1 overflow-y-auto space-y-2 pr-1 custom-scrollbar">
                                {/* Stock / Warehouse Option */}
                                <button
                                    onClick={() => onUnassign(tool.id)}
                                    className={`w-full flex items-center gap-3 p-3 rounded-xl transition-all group text-left border ${tool.status === 'available'
                                        ? 'bg-emerald-500/10 border-emerald-500/30 ring-1 ring-emerald-500/20'
                                        : 'bg-white/5 border-white/10 hover:bg-white/10 hover:border-white/20'
                                        }`}
                                >
                                    <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center text-emerald-400 shadow-sm border border-emerald-500/20">
                                        <Check size={20} className={tool.status === 'available' ? 'opacity-100' : 'opacity-0'} />
                                        {tool.status !== 'available' && <span className="text-lg">🏢</span>}
                                    </div>
                                    <div className="flex-1">
                                        <p className="text-sm font-bold text-white group-hover:text-emerald-400 transition-colors">Stock (Magasin)</p>
                                        <p className="text-[10px] text-gray-400">Remettre l'équipement au dépôt</p>
                                    </div>
                                    {tool.status === 'available' && (
                                        <span className="text-[10px] bg-emerald-500 text-white px-2 py-0.5 rounded-full font-bold uppercase tracking-wider">Actuel</span>
                                    )}
                                </button>

                                <div className="py-2 flex items-center gap-3">
                                    <div className="h-px bg-white/10 flex-1"></div>
                                    <span className="text-[10px] text-gray-500 font-bold uppercase tracking-widest">Ou Transférer à</span>
                                    <div className="h-px bg-white/10 flex-1"></div>
                                </div>

                                {filteredTechs.map(tech => (
                                    <button
                                        key={tech.id}
                                        onClick={() => onAssign(tool.id, tech.id)}
                                        className="w-full flex items-center gap-3 p-2 rounded-xl hover:bg-white/5 transition-colors group text-left border border-transparent hover:border-white/5"
                                    >
                                        <div
                                            className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white shadow-sm"
                                            style={{ backgroundColor: tech.color }}
                                        >
                                            {tech.name.charAt(0)}
                                        </div>
                                        <div className="flex-1">
                                            <p className="text-sm font-medium text-white group-hover:text-primary transition-colors">{tech.name}</p>
                                            <p className="text-[10px] text-gray-500">{tech.service}</p>
                                        </div>
                                        {tool.assignedTo === tech.id && (
                                            <Check size={16} className="text-primary" />
                                        )}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
};
